import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Database, Maximize2, Minimize2, BookmarkPlus, FolderOpen, Copy, Globe } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { sendSurveyNotifications } from "@/lib/sendSurveyNotifications";
import { Constants } from "@/integrations/supabase/types";
import { validateSurveyForPublish, formatPublishErrors } from "@/lib/surveyValidation";

const DB_FIELD_SOURCES = [
  { value: "semester", label: "Semester (from Modules)" },
  { value: "class", label: "Class (from Courses)" },
  { value: "module", label: "Module (linked to Class & Semester)" },
  { value: "course", label: "Program" },
] as const;

type DbFieldSource = typeof DB_FIELD_SOURCES[number]["value"];

interface QuestionDraft {
  id: string;
  question_text: string;
  question_type: string;
  options: any;
  is_required: boolean;
  sort_order: number;
  question_text_sw: string;
  options_sw: any;
}

interface SurveyBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editSurveyId?: string | null;
  onSaved: () => void;
}

const QUESTION_TYPES = [
  { value: "short_text", label: "Short Text" },
  { value: "long_text", label: "Long Text" },
  { value: "single_choice", label: "Single Choice" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "dropdown", label: "Dropdown" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "linear_scale", label: "Linear Scale" },
  { value: "rating", label: "Rating (Stars)" },
  { value: "file_upload", label: "File Upload" },
  { value: "checkbox_grid", label: "Checkbox Grid" },
  { value: "mcq_grid", label: "Multiple Choice Grid" },
  { value: "module_availability", label: "Tutor Available Time" },
];

const ALL_ROLES = Constants.public.Enums.app_role;

const getDefaultOptions = (type: string) => {
  if (type === "single_choice" || type === "multiple_choice" || type === "dropdown") return [];
  if (type === "linear_scale") return ["1", "5", "", ""];
  if (type === "checkbox_grid" || type === "mcq_grid") return { rows: [], columns: [] };
  return [];
};

const OFFICE_OPTIONS = [
  "Office of the Principal",
  "Office of the Deputy Principal",
  "Office of the Academic",
  "Office of Administration and Registrar",
  "Office of Examination",
  "Office of Student Affairs",
];

const SurveyBuilder = ({ open, onOpenChange, editSurveyId, onSaved }: SurveyBuilderProps) => {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [titleSw, setTitleSw] = useState("");
  const [descriptionSw, setDescriptionSw] = useState("");
  const [enableSwahili, setEnableSwahili] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [publicSlug, setPublicSlug] = useState("");
  const [targetRoles, setTargetRoles] = useState<string[]>([]);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [releasedBy, setReleasedBy] = useState("");
  const [inquiryPhone, setInquiryPhone] = useState("");

  // For module-class and module-semester linking
  const [moduleClassFilters, setModuleClassFilters] = useState<Record<number, string>>({});
  const [moduleSemesterFilters, setModuleSemesterFilters] = useState<Record<number, string>>({});
  const [availableClasses, setAvailableClasses] = useState<string[]>([]);
  const [availableSemesters, setAvailableSemesters] = useState<string[]>([]);

  // Saved option sets
  const [savedOptionSets, setSavedOptionSets] = useState<{ id: string; name: string; options: string[] }[]>([]);
  const [saveSetName, setSaveSetName] = useState("");
  const [savingSetForQuestion, setSavingSetForQuestion] = useState<number | null>(null);
  const [loadingSetForQuestion, setLoadingSetForQuestion] = useState<number | null>(null);

  // Fetch available classes for module linking
  useEffect(() => {
    if (open) {
      fetchClasses();
      fetchSemesters();
      fetchSavedOptionSets();
    }
  }, [open]);

  const fetchClasses = async () => {
    const [{ data: modData }, { data: courseData }] = await Promise.all([
      supabase.from("modules").select("class"),
      supabase.from("courses").select("class"),
    ]);
    const all = [
      ...((modData || []).map(r => r.class).filter(Boolean) as string[]),
      ...((courseData || []).map(r => r.class).filter(Boolean) as string[]),
    ];
    const unique = [...new Set(all)].sort();
    setAvailableClasses(unique);
  };

  const fetchSemesters = async () => {
    const [{ data: modData }, { data: courseData }] = await Promise.all([
      supabase.from("modules").select("semester"),
      supabase.from("courses").select("semester"),
    ]);
    const all = [
      ...((modData || []).map(r => r.semester).filter(Boolean) as string[]),
      ...((courseData || []).map(r => r.semester).filter(Boolean) as string[]),
    ];
    setAvailableSemesters([...new Set(all)].sort());
  };

  const fetchSavedOptionSets = async () => {
    const { data } = await supabase
      .from("saved_option_sets")
      .select("id, name, options")
      .order("created_at", { ascending: false });
    if (data) {
      setSavedOptionSets(data.map(d => ({
        id: d.id,
        name: d.name,
        options: Array.isArray(d.options) ? d.options as string[] : [],
      })));
    }
  };

  useEffect(() => {
    if (open && editSurveyId) {
      loadSurvey(editSurveyId);
    } else if (open && !editSurveyId) {
      resetForm();
    }
  }, [open, editSurveyId]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setTitleSw("");
    setDescriptionSw("");
    setEnableSwahili(false);
    setIsPublic(false);
    setPublicSlug("");
    setTargetRoles([]);
    setQuestions([]);
    setModuleClassFilters({});
    setModuleSemesterFilters({});
    setReleasedBy("");
    setInquiryPhone("");
  };

  const loadSurvey = async (id: string) => {
    setLoading(true);
    const { data: survey } = await supabase.from("surveys").select("*").eq("id", id).single();
    if (survey) {
      setTitle(survey.title);
      setDescription(survey.description || "");
      setTitleSw((survey as any).title_sw || "");
      setDescriptionSw((survey as any).description_sw || "");
      setEnableSwahili(!!((survey as any).title_sw || (survey as any).description_sw));
      setIsPublic(survey.is_public);
      setPublicSlug(survey.public_slug || "");
      setTargetRoles(survey.target_roles || []);
      setReleasedBy((survey as any).released_by || "");
      setInquiryPhone((survey as any).inquiry_phone || "");
    }
    const { data: qs } = await supabase
      .from("survey_questions")
      .select("*")
      .eq("survey_id", id)
      .order("sort_order");
    if (qs) {
      const classFilters: Record<number, string> = {};
      const semFilters: Record<number, string> = {};
      setQuestions(qs.map((q, i) => {
        // Restore class and semester filters for module source
        if (typeof q.options === "object" && !Array.isArray(q.options) && q.options?.source === "module") {
          if (q.options?.class_filter) classFilters[i] = String(q.options.class_filter);
          if (q.options?.semester_filter) semFilters[i] = String(q.options.semester_filter);
        }
        return {
          id: q.id,
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options || getDefaultOptions(q.question_type),
          is_required: q.is_required,
          sort_order: q.sort_order,
          question_text_sw: (q as any).question_text_sw || "",
          options_sw: (q as any).options_sw || null,
        };
      }));
      setModuleClassFilters(classFilters);
      setModuleSemesterFilters(semFilters);
    }
    setLoading(false);
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, {
      id: crypto.randomUUID(),
      question_text: "",
      question_type: "short_text",
      options: [],
      is_required: false,
      sort_order: prev.length,
      question_text_sw: "",
      options_sw: null,
    }]);
  };

  const updateQuestion = (index: number, updates: Partial<QuestionDraft>) => {
    setQuestions(prev => prev.map((q, i) => i === index ? { ...q, ...updates } : q));
  };

  const handleTypeChange = (index: number, newType: string) => {
    updateQuestion(index, { question_type: newType, options: getDefaultOptions(newType) });
  };

  const removeQuestion = (index: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, sort_order: i })));
  };

  const moveQuestion = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    const updated = [...questions];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setQuestions(updated.map((q, i) => ({ ...q, sort_order: i })));
  };

  const duplicateQuestion = (index: number) => {
    const original = questions[index];
    const clone: QuestionDraft = {
      ...original,
      id: crypto.randomUUID(),
      question_text: `${original.question_text} (copy)`,
      options: JSON.parse(JSON.stringify(original.options)),
      question_text_sw: original.question_text_sw ? `${original.question_text_sw} (nakala)` : "",
      options_sw: original.options_sw ? JSON.parse(JSON.stringify(original.options_sw)) : null,
    };
    const updated = [...questions];
    updated.splice(index + 1, 0, clone);
    setQuestions(updated.map((q, i) => ({ ...q, sort_order: i })));
  };

  const addQuestionAfter = (index: number) => {
    const newQ: QuestionDraft = {
      id: crypto.randomUUID(),
      question_text: "",
      question_type: "short_text",
      options: [],
      is_required: false,
      sort_order: index + 1,
      question_text_sw: "",
      options_sw: null,
    };
    const updated = [...questions];
    updated.splice(index + 1, 0, newQ);
    setQuestions(updated.map((q, i) => ({ ...q, sort_order: i })));
  };

  const addOption = (qIndex: number) => {
    const q = questions[qIndex];
    const opts = Array.isArray(q.options) ? q.options : [];
    updateQuestion(qIndex, { options: [...opts, ""] });
  };

  const updateOption = (qIndex: number, optIndex: number, value: string) => {
    const q = questions[qIndex];
    const newOpts = [...(q.options as string[])];
    newOpts[optIndex] = value;
    updateQuestion(qIndex, { options: newOpts });
  };

  const removeOption = (qIndex: number, optIndex: number) => {
    const q = questions[qIndex];
    updateQuestion(qIndex, { options: (q.options as string[]).filter((_, i) => i !== optIndex) });
  };

  const updateGridField = (qIndex: number, field: "rows" | "columns", values: string[]) => {
    const q = questions[qIndex];
    const grid = typeof q.options === "object" && !Array.isArray(q.options) ? q.options : { rows: [], columns: [] };
    updateQuestion(qIndex, { options: { ...grid, [field]: values } });
  };

  const addGridItem = (qIndex: number, field: "rows" | "columns") => {
    const q = questions[qIndex];
    const grid = typeof q.options === "object" && !Array.isArray(q.options) ? q.options : { rows: [], columns: [] };
    updateGridField(qIndex, field, [...(grid[field] || []), ""]);
  };

  const updateGridItem = (qIndex: number, field: "rows" | "columns", itemIndex: number, value: string) => {
    const q = questions[qIndex];
    const grid = typeof q.options === "object" && !Array.isArray(q.options) ? q.options : { rows: [], columns: [] };
    const items = [...(grid[field] || [])];
    items[itemIndex] = value;
    updateGridField(qIndex, field, items);
  };

  const removeGridItem = (qIndex: number, field: "rows" | "columns", itemIndex: number) => {
    const q = questions[qIndex];
    const grid = typeof q.options === "object" && !Array.isArray(q.options) ? q.options : { rows: [], columns: [] };
    updateGridField(qIndex, field, (grid[field] || []).filter((_: string, i: number) => i !== itemIndex));
  };

  const toggleRole = (role: string) => {
    setTargetRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const fetchDbOptions = async (field: string, classFilter?: string, semesterFilter?: string): Promise<string[]> => {
    let values: string[] = [];
    if (field === "semester") {
      const [{ data: modData }, { data: courseData }] = await Promise.all([
        supabase.from("modules").select("semester"),
        supabase.from("courses").select("semester"),
      ]);
      const all = [
        ...((modData || []).map(r => r.semester)),
        ...((courseData || []).map(r => r.semester).filter(Boolean) as string[]),
      ];
      values = [...new Set(all)].sort();
    } else if (field === "class") {
      const [{ data: modData }, { data: courseData }] = await Promise.all([
        supabase.from("modules").select("class"),
        supabase.from("courses").select("class"),
      ]);
      const all = [
        ...((modData || []).map(r => r.class).filter(Boolean) as string[]),
        ...((courseData || []).map(r => r.class).filter(Boolean) as string[]),
      ];
      values = [...new Set(all)].sort();
    } else if (field === "module") {
      let query = supabase.from("modules").select("module_code, module_name, class, semester").order("module_code");
      if (classFilter) {
        query = query.eq("class", classFilter);
      }
      if (semesterFilter) {
        query = query.eq("semester", semesterFilter);
      }
      const { data } = await query;
      values = (data || []).map(r => `${r.module_code} - ${r.module_name}`);
    } else if (field === "course") {
      const { data } = await supabase.from("courses").select("course_name").order("course_name");
      values = [...new Set((data || []).map(r => r.course_name))];
    }
    return values;
  };

  const handleDbSourceChange = async (qIndex: number, field: string) => {
    if (field === "module") {
      // Don't fetch yet — wait for class/semester selection
      updateQuestion(qIndex, { options: { source: "module", class_filter: "", semester_filter: "", values: [] } });
      return;
    }
    updateQuestion(qIndex, { options: { source: field, values: [] } });
    try {
      const values = await fetchDbOptions(field);
      updateQuestion(qIndex, { options: { source: field, values } });
      toast.success(`Loaded ${values.length} options from ${field}`);
    } catch {
      toast.error("Failed to fetch options");
    }
  };

  const fetchModulesWithFilters = async (qIndex: number, cls?: string, sem?: string) => {
    try {
      const values = await fetchDbOptions("module", cls, sem);
      const currentOpts = questions[qIndex]?.options || {};
      const source = typeof currentOpts === "object" && !Array.isArray(currentOpts) ? currentOpts : {};
      updateQuestion(qIndex, {
        options: { ...source, source: "module", class_filter: cls || "", semester_filter: sem || "", values }
      });
      const filterDesc = [cls, sem].filter(Boolean).join(" / ") || "all";
      toast.success(`Loaded ${values.length} modules for ${filterDesc}`);
    } catch {
      toast.error("Failed to fetch modules");
    }
  };

  const handleModuleClassSelect = async (qIndex: number, cls: string) => {
    setModuleClassFilters(prev => ({ ...prev, [qIndex]: cls }));
    const sem = moduleSemesterFilters[qIndex] || "";
    await fetchModulesWithFilters(qIndex, cls, sem);
  };

  const handleModuleSemesterSelect = async (qIndex: number, sem: string) => {
    setModuleSemesterFilters(prev => ({ ...prev, [qIndex]: sem }));
    const cls = moduleClassFilters[qIndex] || "";
    await fetchModulesWithFilters(qIndex, cls, sem);
  };

  const handleSaveOptionSet = async (qIndex: number) => {
    const q = questions[qIndex];
    const opts = Array.isArray(q.options) ? (q.options as string[]).filter(o => o.trim()) : [];
    if (opts.length === 0) { toast.error("Add some options first"); return; }
    if (!saveSetName.trim()) { toast.error("Enter a name for this option set"); return; }

    const { error } = await supabase.from("saved_option_sets").insert({
      name: saveSetName.trim(),
      options: opts as any,
      created_by: user!.id,
    });
    if (error) { toast.error(error.message); return; }
    toast.success("Options saved for reuse!");
    setSaveSetName("");
    setSavingSetForQuestion(null);
    fetchSavedOptionSets();
  };

  const handleLoadOptionSet = (qIndex: number, setId: string) => {
    const optSet = savedOptionSets.find(s => s.id === setId);
    if (optSet) {
      updateQuestion(qIndex, { options: [...optSet.options] });
      toast.success(`Loaded "${optSet.name}" options`);
    }
    setLoadingSetForQuestion(null);
  };

  const handleSave = async (publish: boolean) => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (questions.length === 0) { toast.error("Add at least one question"); return; }
    const emptyQ = questions.find(q => !q.question_text.trim());
    if (emptyQ) { toast.error("All questions must have text"); return; }

    // Stricter validation when publishing
    if (publish) {
      const result = validateSurveyForPublish({
        title,
        description,
        is_public: isPublic,
        public_slug: publicSlug,
        target_roles: targetRoles,
        inquiry_phone: inquiryPhone,
        released_by: releasedBy,
        questions: questions.map(q => ({
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options,
          is_required: q.is_required,
        })),
      });
      if (result.ok === false) {
        toast.error(formatPublishErrors(result.errors), { duration: 8000 });
        return;
      }
    }

    const slugValue = isPublic && publicSlug.trim() ? publicSlug.trim() : null;

    // Validate slug uniqueness
    if (slugValue) {
      let query = supabase.from("surveys").select("id").eq("public_slug", slugValue);
      if (editSurveyId) query = query.neq("id", editSurveyId);
      const { data: existing } = await query;
      if (existing && existing.length > 0) {
        toast.error(`The slug "${slugValue}" is already taken. Choose a different one.`);
        return;
      }
    }

    setSaving(true);
    try {
      const surveyData: any = {
        title: title.trim(),
        description: description.trim() || null,
        title_sw: enableSwahili && titleSw.trim() ? titleSw.trim() : null,
        description_sw: enableSwahili && descriptionSw.trim() ? descriptionSw.trim() : null,
        is_public: isPublic,
        public_slug: slugValue,
        target_roles: targetRoles,
        status: publish ? "published" : "draft",
        created_by: user!.id,
        released_by: releasedBy || null,
        inquiry_phone: inquiryPhone.trim() || null,
      };
      if (publish) {
        surveyData.published_at = new Date().toISOString();
      }

      let surveyId = editSurveyId;

      if (editSurveyId) {
        const { error } = await supabase.from("surveys").update(surveyData).eq("id", editSurveyId);
        if (error) throw error;
        const { error: delError } = await supabase.from("survey_questions").delete().eq("survey_id", editSurveyId);
        if (delError) throw new Error(`Failed to clear old questions: ${delError.message}`);
      } else {
        const { data, error } = await supabase.from("surveys").insert(surveyData).select("id").single();
        if (error) throw error;
        surveyId = data.id;
      }

      const choiceTypes = ["single_choice", "multiple_choice", "dropdown"];
      const gridTypes = ["checkbox_grid", "mcq_grid"];

      const questionRows = questions.map((q, i) => {
        let options: any = null;
        if (choiceTypes.includes(q.question_type)) {
          if (typeof q.options === "object" && !Array.isArray(q.options) && q.options?.source) {
            options = q.options;
          } else {
            options = (q.options as string[]).filter((o: string) => o.trim());
          }
        } else if (gridTypes.includes(q.question_type) || q.question_type === "linear_scale") {
          options = q.options;
        }
        return {
          survey_id: surveyId!,
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          options,
          is_required: q.is_required,
          sort_order: i,
          question_text_sw: enableSwahili && q.question_text_sw?.trim() ? q.question_text_sw.trim() : null,
          options_sw: enableSwahili && q.options_sw ? q.options_sw : null,
        };
      });

      const { error: qError } = await supabase.from("survey_questions").insert(questionRows);
      if (qError) throw qError;

      toast.success(publish ? "Survey published!" : "Survey saved as draft");

      // Send notifications to targeted roles when publishing
      if (publish && targetRoles.length > 0) {
        sendSurveyNotifications(
          title.trim(),
          targetRoles,
          slugValue || surveyId!,
          isPublic
        );
      }

      onOpenChange(false);
      onSaved();
    } catch (err: any) {
      toast.error(err.message || "Failed to save survey");
    } finally {
      setSaving(false);
    }
  };

  const hasChoiceOptions = (type: string) => ["single_choice", "multiple_choice", "dropdown"].includes(type);
  const isGridType = (type: string) => ["checkbox_grid", "mcq_grid"].includes(type);

  const dialogClasses = isFullscreen
    ? "max-w-[95vw] w-full h-[95vh] flex flex-col"
    : "max-w-2xl max-h-[90vh] overflow-y-auto";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogClasses}>
        <DialogHeader className="flex flex-row items-center justify-between pr-8">
          <DialogTitle>{editSurveyId ? "Edit Survey" : "Create Survey"}</DialogTitle>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => setIsFullscreen(prev => !prev)}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Loading...</p>
        ) : (
          <div className={`space-y-4 ${isFullscreen ? "overflow-y-auto flex-1 pr-1" : ""}`}>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Survey title" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" rows={2} />
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={enableSwahili} onCheckedChange={setEnableSwahili} />
              <Label className="flex items-center gap-1.5">
                <Globe className="h-4 w-4" />
                Enable Swahili translation
              </Label>
            </div>

            {enableSwahili && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Title (Swahili)</Label>
                  <Input value={titleSw} onChange={e => setTitleSw(e.target.value)} placeholder="Kichwa cha dodoso" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Description (Swahili)</Label>
                  <Textarea value={descriptionSw} onChange={e => setDescriptionSw(e.target.value)} placeholder="Maelezo ya dodoso" rows={2} />
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch checked={isPublic} onCheckedChange={setIsPublic} />
              <Label>Public survey (accessible without login)</Label>
            </div>

            {isPublic && (
              <div className="space-y-2">
                <Label>Public Slug (URL path)</Label>
                <Input value={publicSlug} onChange={e => setPublicSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="my-survey" />
                <p className="text-xs text-muted-foreground">Access at /survey/{publicSlug || "..."}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Target Roles (leave empty for all authenticated users)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ALL_ROLES.map(role => (
                  <label key={role} className="flex items-center gap-2 cursor-pointer text-xs">
                    <Checkbox checked={targetRoles.includes(role)} onCheckedChange={() => toggleRole(role)} />
                    <span>{role.replace(/_/g, " ")}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Released by</Label>
              <Select value={releasedBy} onValueChange={setReleasedBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Select office (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {OFFICE_OPTIONS.map(office => (
                    <SelectItem key={office} value={office}>{office}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Inquiry Phone Number</Label>
              <Input value={inquiryPhone} onChange={e => setInquiryPhone(e.target.value)} placeholder="e.g. +255 123 456 789" />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Questions</Label>
                <Button type="button" size="sm" variant="outline" onClick={addQuestion}>
                  <Plus className="h-4 w-4 mr-1" /> Add Question
                </Button>
              </div>

              <div className="space-y-3">
                {questions.map((q, idx) => (
                  <Card key={q.id}>
                    <CardContent className="pt-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-mono">Q{idx + 1}</span>
                        <Input
                          value={q.question_text}
                          onChange={e => updateQuestion(idx, { question_text: e.target.value })}
                          placeholder="Question text"
                          className="flex-1"
                        />
                        <Button type="button" size="icon" variant="ghost" onClick={() => moveQuestion(idx, "up")} disabled={idx === 0}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" onClick={() => moveQuestion(idx, "down")} disabled={idx === questions.length - 1}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" onClick={() => duplicateQuestion(idx)} title="Duplicate question">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" onClick={() => addQuestionAfter(idx)} title="Add question below">
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeQuestion(idx)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>

                      {/* Swahili question text */}
                      {enableSwahili && (
                        <div className="pl-4 border-l-2 border-primary/20">
                          <Input
                            value={q.question_text_sw}
                            onChange={e => updateQuestion(idx, { question_text_sw: e.target.value })}
                            placeholder="Swahili: Maandishi ya swali"
                            className="text-sm"
                          />
                        </div>
                      )}

                      <div className="flex items-center gap-3">
                        <Select value={q.question_type} onValueChange={v => handleTypeChange(idx, v)}>
                          <SelectTrigger className="w-52">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {QUESTION_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <Checkbox checked={q.is_required} onCheckedChange={v => updateQuestion(idx, { is_required: !!v })} />
                          Required
                        </label>
                      </div>

                      {/* Choice options (single_choice, multiple_choice, dropdown) */}
                      {hasChoiceOptions(q.question_type) && (() => {
                        const isDbSource = typeof q.options === "object" && !Array.isArray(q.options) && "source" in (q.options || {});
                        const manualOpts = Array.isArray(q.options) ? q.options : [];
                        const dbValues = isDbSource ? (q.options?.values || []) : [];

                        return (
                          <div className="space-y-2 pl-4">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs whitespace-nowrap">Options Source:</Label>
                              <Select
                                value={isDbSource ? "database" : "manual"}
                                onValueChange={v => {
                                  if (v === "manual") {
                                    updateQuestion(idx, { options: [] });
                                  } else {
                                    updateQuestion(idx, { options: { source: "", values: [] } });
                                  }
                                }}
                              >
                                <SelectTrigger className="h-8 w-40 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="manual">Manual</SelectItem>
                                  <SelectItem value="database">From Database</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {!isDbSource ? (
                              <>
                                {manualOpts.map((opt: string, oi: number) => (
                                  <div key={oi} className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <Input
                                        value={opt}
                                        onChange={e => updateOption(idx, oi, e.target.value)}
                                        placeholder={`Option ${oi + 1}`}
                                        className="h-8 text-sm"
                                      />
                                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeOption(idx, oi)}>
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    {enableSwahili && (
                                      <Input
                                        value={(Array.isArray(q.options_sw) ? q.options_sw[oi] : "") || ""}
                                        onChange={e => {
                                          const swOpts = Array.isArray(q.options_sw) ? [...q.options_sw] : Array(manualOpts.length).fill("");
                                          while (swOpts.length < manualOpts.length) swOpts.push("");
                                          swOpts[oi] = e.target.value;
                                          updateQuestion(idx, { options_sw: swOpts });
                                        }}
                                        placeholder={`Swahili: Chaguo ${oi + 1}`}
                                        className="h-8 text-sm ml-4 border-primary/20"
                                      />
                                    )}
                                  </div>
                                ))}
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Button type="button" size="sm" variant="ghost" onClick={() => addOption(idx)} className="text-xs">
                                    <Plus className="h-3 w-3 mr-1" /> Add Option
                                  </Button>

                                  {/* Save manual options */}
                                  {manualOpts.length > 0 && savingSetForQuestion !== idx && (
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setSavingSetForQuestion(idx)} className="text-xs">
                                      <BookmarkPlus className="h-3 w-3 mr-1" /> Save Options
                                    </Button>
                                  )}

                                  {/* Load saved options */}
                                  {loadingSetForQuestion !== idx && savedOptionSets.length > 0 && (
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setLoadingSetForQuestion(idx)} className="text-xs">
                                      <FolderOpen className="h-3 w-3 mr-1" /> Load Saved
                                    </Button>
                                  )}
                                </div>

                                {/* Save option set inline form */}
                                {savingSetForQuestion === idx && (
                                  <div className="flex items-center gap-2 bg-muted/50 rounded p-2">
                                    <Input
                                      value={saveSetName}
                                      onChange={e => setSaveSetName(e.target.value)}
                                      placeholder="Name for this option set"
                                      className="h-8 text-sm flex-1"
                                    />
                                    <Button type="button" size="sm" onClick={() => handleSaveOptionSet(idx)} className="text-xs">
                                      Save
                                    </Button>
                                    <Button type="button" size="sm" variant="ghost" onClick={() => { setSavingSetForQuestion(null); setSaveSetName(""); }} className="text-xs">
                                      Cancel
                                    </Button>
                                  </div>
                                )}

                                {/* Load option set selector */}
                                {loadingSetForQuestion === idx && (
                                  <div className="bg-muted/50 rounded p-2 space-y-1">
                                    <p className="text-xs text-muted-foreground">Select a saved option set:</p>
                                    {savedOptionSets.map(s => (
                                      <Button
                                        key={s.id}
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleLoadOptionSet(idx, s.id)}
                                        className="text-xs w-full justify-start"
                                      >
                                        {s.name} ({s.options.length} options)
                                      </Button>
                                    ))}
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setLoadingSetForQuestion(null)} className="text-xs">
                                      Cancel
                                    </Button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="space-y-2">
                                <Select
                                  value={q.options?.source || ""}
                                  onValueChange={(field: string) => handleDbSourceChange(idx, field)}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Select database field..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DB_FIELD_SOURCES.map(s => (
                                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                {/* Module class & semester filters */}
                                {q.options?.source === "module" && (
                                  <div className="space-y-2">
                                    <div className="space-y-1">
                                      <Label className="text-xs">Filter by Class:</Label>
                                      <Select
                                        value={moduleClassFilters[idx] || ""}
                                        onValueChange={(cls) => handleModuleClassSelect(idx, cls)}
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue placeholder="Select a class..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {availableClasses.map(cls => (
                                            <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Filter by Semester:</Label>
                                      <Select
                                        value={moduleSemesterFilters[idx] || ""}
                                        onValueChange={(sem) => handleModuleSemesterSelect(idx, sem)}
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue placeholder="Select a semester..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {availableSemesters.map(sem => (
                                            <SelectItem key={sem} value={sem}>{sem}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                )}

                                {dbValues.length > 0 && (
                                  <div className="bg-muted/50 rounded p-2">
                                    <p className="text-xs text-muted-foreground mb-1">
                                      <Database className="h-3 w-3 inline mr-1" />
                                      {dbValues.length} options loaded:
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {dbValues.map((v: string, i: number) => (
                                        <span key={i} className="text-xs bg-background border rounded px-1.5 py-0.5">{v}</span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Linear scale config */}
                      {q.question_type === "linear_scale" && (
                        <div className="space-y-2 pl-4">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs w-16">Min</Label>
                            <Input
                              type="number"
                              value={(q.options as string[])[0] || "1"}
                              onChange={e => {
                                const opts = [...(q.options as string[])];
                                opts[0] = e.target.value;
                                updateQuestion(idx, { options: opts });
                              }}
                              className="h-8 w-20 text-sm"
                            />
                            <Input
                              value={(q.options as string[])[2] || ""}
                              onChange={e => {
                                const opts = [...(q.options as string[])];
                                opts[2] = e.target.value;
                                updateQuestion(idx, { options: opts });
                              }}
                              placeholder="Low label (e.g. Not likely)"
                              className="h-8 text-sm flex-1"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs w-16">Max</Label>
                            <Input
                              type="number"
                              value={(q.options as string[])[1] || "5"}
                              onChange={e => {
                                const opts = [...(q.options as string[])];
                                opts[1] = e.target.value;
                                updateQuestion(idx, { options: opts });
                              }}
                              className="h-8 w-20 text-sm"
                            />
                            <Input
                              value={(q.options as string[])[3] || ""}
                              onChange={e => {
                                const opts = [...(q.options as string[])];
                                opts[3] = e.target.value;
                                updateQuestion(idx, { options: opts });
                              }}
                              placeholder="High label (e.g. Very likely)"
                              className="h-8 text-sm flex-1"
                            />
                          </div>
                        </div>
                      )}

                      {/* Grid config (checkbox_grid & mcq_grid) */}
                      {isGridType(q.question_type) && (() => {
                        const grid = typeof q.options === "object" && !Array.isArray(q.options) ? q.options : { rows: [], columns: [] };
                        return (
                          <div className="space-y-3 pl-4">
                            <div>
                              <Label className="text-xs font-medium">Rows</Label>
                              {(grid.rows || []).map((r: string, ri: number) => (
                                <div key={ri} className="space-y-1 mt-1">
                                  <div className="flex items-center gap-2">
                                    <Input value={r} onChange={e => updateGridItem(idx, "rows", ri, e.target.value)} placeholder={`Row ${ri + 1}`} className="h-8 text-sm" />
                                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeGridItem(idx, "rows", ri)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  {enableSwahili && (
                                    <Input
                                      value={(() => { const sw = q.options_sw && typeof q.options_sw === "object" && !Array.isArray(q.options_sw) ? q.options_sw : {}; return (sw.rows || [])[ri] || ""; })()}
                                      onChange={e => {
                                        const sw = q.options_sw && typeof q.options_sw === "object" && !Array.isArray(q.options_sw) ? { ...q.options_sw } : { rows: [], columns: [] };
                                        const swRows = [...(sw.rows || [])];
                                        while (swRows.length <= ri) swRows.push("");
                                        swRows[ri] = e.target.value;
                                        updateQuestion(idx, { options_sw: { ...sw, rows: swRows } });
                                      }}
                                      placeholder={`Swahili: Mstari ${ri + 1}`}
                                      className="h-8 text-sm ml-4 border-primary/20"
                                    />
                                  )}
                                </div>
                              ))}
                              <Button type="button" size="sm" variant="ghost" onClick={() => addGridItem(idx, "rows")} className="text-xs mt-1">
                                <Plus className="h-3 w-3 mr-1" /> Add Row
                              </Button>
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Columns</Label>
                              {(grid.columns || []).map((c: string, ci: number) => (
                                <div key={ci} className="space-y-1 mt-1">
                                  <div className="flex items-center gap-2">
                                    <Input value={c} onChange={e => updateGridItem(idx, "columns", ci, e.target.value)} placeholder={`Column ${ci + 1}`} className="h-8 text-sm" />
                                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeGridItem(idx, "columns", ci)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  {enableSwahili && (
                                    <Input
                                      value={(() => { const sw = q.options_sw && typeof q.options_sw === "object" && !Array.isArray(q.options_sw) ? q.options_sw : {}; return (sw.columns || [])[ci] || ""; })()}
                                      onChange={e => {
                                        const sw = q.options_sw && typeof q.options_sw === "object" && !Array.isArray(q.options_sw) ? { ...q.options_sw } : { rows: [], columns: [] };
                                        const swCols = [...(sw.columns || [])];
                                        while (swCols.length <= ci) swCols.push("");
                                        swCols[ci] = e.target.value;
                                        updateQuestion(idx, { options_sw: { ...sw, columns: swCols } });
                                      }}
                                      placeholder={`Swahili: Safu ${ci + 1}`}
                                      className="h-8 text-sm ml-4 border-primary/20"
                                    />
                                  )}
                                </div>
                              ))}
                              <Button type="button" size="sm" variant="ghost" onClick={() => addGridItem(idx, "columns")} className="text-xs mt-1">
                                <Plus className="h-3 w-3 mr-1" /> Add Column
                              </Button>
                            </div>
                          </div>
                        );
                      })()}

                      {q.question_type === "rating" && (
                        <p className="text-xs text-muted-foreground pl-4">Respondents will select 1–5 stars</p>
                      )}
                      {q.question_type === "file_upload" && (
                        <p className="text-xs text-muted-foreground pl-4">Respondents can upload a file (placeholder — no storage wired yet)</p>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {questions.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No questions yet. Click "Add Question" to start.</p>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => handleSave(false)} disabled={saving} className="flex-1">
                <Save className="h-4 w-4 mr-1" /> Save Draft
              </Button>
              <Button type="button" onClick={() => handleSave(true)} disabled={saving} className="flex-1">
                Publish
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SurveyBuilder;
